// Define an interface for configuration options
export interface ConfigurationOptios {
	preserveEmptyArrays?: boolean;
	preserveEmptyObjects?: boolean;
	preserveDirtyValues?: boolean;
	dirtyValues?: Record<string, any>;
}

/**
 * Checks if a path exists in the dirty values object
 * @param dirtyValues - The dirty values structure
 * @param path - Array of keys representing the path to check
 * @returns true if the path exists in dirty values
 */
function isDirtyPath(dirtyValues: Record<string, any> | undefined, path: string[]): boolean {
	if (!dirtyValues || path.length === 0) {
		return false;
	}

	let current = dirtyValues;

	for (const key of path) {
		if (current && typeof current === 'object' && key in current) {
			current = current[key];
		} else {
			return false;
		}
	}

	return true;
}

/**
 * Determines whether a value is empty.
 * This function will recursively clean arrays and objects to determine emptiness.
 */
export function isEmpty(value: any, options?: ConfigurationOptios, currentPath: string[] = []): boolean {
	if (value === null || value === undefined) return true;

	if (typeof value === 'string' && value.trim() === '') return true;

	if (Array.isArray(value)) {
		// Clean each element in the array recursively.
		const cleanedArray = value
			.map((item, index) => sanitizeFormData(item, options, [...currentPath, String(index)]))
			.filter((item, index) => !isEmpty(item, options, [...currentPath, String(index)]));
		// If preserving empty arrays is enabled, treat the array as non-empty.
		if (options?.preserveEmptyArrays) return false;
		return cleanedArray.length === 0;
	}

	if (typeof value === 'object') {
		// Clean each property in the object recursively.
		const cleanedObject = Object.fromEntries(
			Object.entries(value)
				.map(([key, val]) => [key, sanitizeFormData(val, options, [...currentPath, String(key)])])
				.filter(([key, val]) => !isEmpty(val, options, [...currentPath, String(key)]))
		);
		// If preserving empty objects is enabled, treat the object as non-empty.
		if (options?.preserveEmptyObjects) return false;
		return Object.keys(cleanedObject).length === 0;
	}

	// For numbers, booleans, or non-empty strings, return false.
	return false;
}

/**
 * Recursively cleans data by removing keys or elements that are empty.
 * It applies the cleaning to nested arrays and objects.
 */
export function sanitizeFormData<T>(data: T, options?: ConfigurationOptios, currentPath: string[] = []): T {
	if (Array.isArray(data)) {
		const cleanedArray = data
			.map((item, index) => sanitizeFormData(item, options, [...currentPath, String(index)]))
			.filter((item, index) => {
				const itemPath = [...currentPath, String(index)];
				// If preserving dirty values, keep items that are in dirty paths
				if (options?.preserveDirtyValues && isDirtyPath(options.dirtyValues, itemPath)) {
					return true;
				}
				return !isEmpty(item, options, itemPath);
			});
		return cleanedArray as unknown as T;
	}

	if (typeof data === 'object' && data !== null) {
		const cleanedObject = Object.fromEntries(
			Object.entries(data)
				.map(([key, value]) => {
					const newPath = [...currentPath, key];
					const sanitizedValue = sanitizeFormData(value, options, newPath);
					return [key, sanitizedValue];
				})
				.filter(([key, value]) => {
					const valuePath = [...currentPath, key];

					// If preserveDirtyValues is enabled and this path is dirty, keep it
					if (options?.preserveDirtyValues && isDirtyPath(options.dirtyValues, valuePath)) {
						return true;
					}

					// Otherwise, filter out empty values
					return !isEmpty(value, options, valuePath);
				})
		);
		return cleanedObject as T;
	}

	return data;
}
